import { SiteUrls } from '@tableau/shared-urls';

import { ServiceLocator } from '../../app/ServiceLocator';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { isPhoneSizedScreen } from '../../app/utils/MobileHelper';
import { Messages } from '../../codegen/Localize';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { HelpService } from '../../ts/services/HelpService';
import { ServerService } from '../../ts/services/ServerService';
import { WelcomeChannelStateProps } from '../components/WelcomeChannel';
import { WelcomeChannelLinks } from './WelcomeChannelLinks';

export namespace WelcomeChannelProps {
  /**
 * Helper function that encapsulates the ServiceLocator calls.
 * This function needs to be called inside a react component
 */
  export const getPropsByRole = (role: string, canWebAuthor: boolean): WelcomeChannelStateProps => {
    const siteUrls = new SpacesSiteUrls({
      siteUrlName: ServiceLocator.get<ServerService>('ServerService').sessionSiteUrlName(),
    });

    const welcomeChannelPropsByRole = new WelcomeChannelPropsByRole(
      siteUrls,
      ServiceLocator.get<HelpService>('HelpService'),
      canWebAuthor);
    return welcomeChannelPropsByRole.getProps(role);
  };
}

class WelcomeChannelPropsByRole {
  private links: WelcomeChannelLinks;
  private canWebAuthor: boolean;

  constructor(siteUrls: SiteUrls, helpService: HelpService, canWebAuthor: boolean) {
    this.links = new WelcomeChannelLinks(siteUrls, helpService);
    this.canWebAuthor = canWebAuthor;
  }

  getProps(role: string): WelcomeChannelStateProps {
    switch (role) {

      case SiteRoles.ServerAdministrator:
      case SiteRoles.SiteAdministratorCreator:
      case SiteRoles.SupportUser:
        return {
          header: Messages.home_welcomeChannel_HeaderAdmin(),
          subHeader: Messages.home_welcomeChannel_SubheaderAdmin(),
          primaryButtonLink: this.createWorkbookIfWebAuthoringEnabled(),
          bottomLinks: [
            this.links.manageProjects(),
            this.links.manageUsers(),
            this.links.downloadDesktop(),
            this.links.downloadPrep(),
          ],
        };

      case SiteRoles.SiteAdministratorExplorer:
        return {
          header: Messages.home_welcomeChannel_HeaderAdmin(),
          subHeader: Messages.home_welcomeChannel_SubheaderAdmin(),
          primaryButtonLink: this.links.manageProjects(),
          bottomLinks: [
            this.links.manageUsers(),
            this.createWorkbookIfWebAuthoringEnabled(),
          ],
        };

      case SiteRoles.Creator:
        return {
          header: Messages.home_welcomeChannel_HeaderNonAdmin(),
          subHeader: Messages.home_welcomeChannel_SubheaderNonAdmin(),
          primaryButtonLink: this.createWorkbookIfWebAuthoringEnabled(),
          bottomLinks: [
            this.links.downloadDesktop(),
            this.links.downloadPrep(),
          ],
        };

      case SiteRoles.Explorer:
      case SiteRoles.ExplorerCanPublish:
        return {
          header: Messages.home_welcomeChannel_HeaderNonAdmin(),
          subHeader: Messages.home_welcomeChannel_SubheaderNonAdmin(),
          primaryButtonLink: this.links.startExploring(),
          bottomLinks: [],
        };

      case SiteRoles.Viewer:
      case SiteRoles.Guest:
        return {
          header: Messages.home_welcomeChannel_HeaderNonAdmin(),
          subHeader: Messages.home_welcomeChannel_SubheaderNonAdmin(),
          primaryButtonLink: this.links.startExploring(),
          bottomLinks: [],
        };

      default:
        return {
          header: Messages.home_welcomeChannel_HeaderNonAdmin(),
          subHeader: Messages.home_welcomeChannel_SubheaderNonAdmin(),
          primaryButtonLink: this.links.startExploring(),
          bottomLinks: [],
        };
    }
  }

  // TFS 960183: Create Workbook needs to respect web authoring enabled/disabled setting
  createWorkbookIfWebAuthoringEnabled() {
    return this.canWebAuthor && !isPhoneSizedScreen() ? this.links.createWorkbook() : this.links.startExploring();
  }
}
